/**
 * Sound definitions. Every game sound is synthesised here from the primitives in
 * synth.ts, driven by the parameters in materials.ts.
 *
 * Why synthesis rather than recorded files (docs/03 §1): a dice game plays its
 * dice sound thousands of times per session. Recorded variants become audible as
 * repetition within minutes; parameterised synthesis never repeats, costs 0 KB,
 * and lets a cursed die or a critical roll bend the same sound instead of needing
 * its own asset.
 */

import {
  BUSES,
  MATERIALS,
  MOTIF_ROOT_HZ,
  RARITY_SIGNATURES,
  TABLE_PROFILE,
  TABLE_RESONANCE_MIX,
  semitonesToHz,
  type BusName,
  type DiceMaterial,
  type Rarity,
} from './materials.js';
import {
  NoiseRng,
  applyFades,
  bandpass,
  createBuffer,
  lowpass,
  mixInto,
  modalRing,
  noiseBurst,
  noiseSweep,
  normalize,
  reverse,
  shimmer as shimmerTone,
  softClip,
  struckTone,
  subDrop,
  type Buffer,
} from './synth.js';

export const SAMPLE_RATE = 48_000; // Audio Bible §6: 48 kHz master

export interface SfxSpec {
  readonly id: string;
  readonly bus: BusName;
  /** Nominal duration, seconds. Informational; render decides the real length. */
  readonly duration: number;
  readonly render: (rng: NoiseRng, opts?: RenderOptions) => Buffer;
}

export interface RenderOptions {
  readonly material?: DiceMaterial;
  readonly rarity?: Rarity;
  /** 1..6, so a critical roll can sound different from a failure. */
  readonly face?: number;
  /** Adds unease, used when Fate Debt is high. */
  readonly cursed?: boolean;
  readonly sampleRate?: number;
}

// ---------------------------------------------------------------------------
// Dice
// ---------------------------------------------------------------------------

/** One contact between die and table. */
function impact(
  sampleRate: number,
  rng: NoiseRng,
  material: DiceMaterial,
  strength: number,
): Buffer {
  const p = MATERIALS[material];
  const out = createBuffer(sampleRate, p.decay * (1 + p.q / 12) * 4 + 0.02);

  // The transient: broadband contact noise, band-limited around the body.
  const click = noiseBurst(sampleRate, rng, p.attack, p.decay * 0.28, p.noise * strength);
  bandpass(click, p.baseFreq * 0.55, p.baseFreq * 7);
  mixInto(out, click, 0);

  // The body: inharmonic modal partials, slightly detuned per impact so no two
  // bounces are identical.
  p.modes.forEach((ratio, i) => {
    const detune = rng.range(0.985, 1.015);
    const freq = p.baseFreq * ratio * detune;
    if (freq > sampleRate / 2) return;
    const gain = (1 - p.noise) * strength * (1 / (1 + i * 1.4));
    mixInto(out, modalRing(sampleRate, freq, p.q, p.decay, gain, out.data.length / sampleRate, rng.range(0, Math.PI)), 0);
  });

  // A fixed share of table resonance, so every material shares one room.
  mixInto(
    out,
    modalRing(
      sampleRate,
      TABLE_PROFILE.baseFreq * rng.range(0.97, 1.03),
      TABLE_PROFILE.q,
      TABLE_PROFILE.decay,
      strength * TABLE_RESONANCE_MIX,
      0.3,
    ),
    0,
  );

  if (p.shimmer) {
    mixInto(out, shimmerTone(sampleRate, p.baseFreq * p.shimmer, p.decay * 1.6, 14, strength * 0.18), 0.004);
  }
  return out;
}

/**
 * A full throw: several bounces at decreasing strength and shortening intervals,
 * because that is what a real die does — it is the settling pattern, not the
 * individual click, that the ear recognises as "a die was thrown".
 */
export function renderDiceRoll(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const material = opts.material ?? 'wood';
  const p = MATERIALS[material];
  const [minB, maxB] = p.bounces;
  const bounces = Math.round(rng.range(minB, maxB + 0.999));

  const out = createBuffer(sampleRate, 1.5);
  let t = 0;
  let gap = rng.range(0.075, 0.115);
  let strength = 1;

  for (let i = 0; i < bounces; i++) {
    mixInto(out, impact(sampleRate, rng, material, strength), t);
    t += gap;
    // Each bounce is weaker and sooner — energy loss, same as the real thing.
    gap *= rng.range(0.58, 0.74);
    strength *= rng.range(0.52, 0.68);
  }

  // The settle: a final soft scrape as the die stops rocking.
  const settle = noiseSweep(sampleRate, rng, 0.05, 1800, 500, 0.07);
  mixInto(out, settle, t);

  if (opts.cursed) {
    // Fate Debt makes the die sound wrong without adding a new asset.
    mixInto(out, shimmerTone(sampleRate, p.baseFreq * 0.5, 0.7, 32, 0.12), 0);
  }
  if (opts.face === 6) {
    // A critical gets a rising tail, the audible half of the pips lighting up.
    mixInto(out, struckTone(sampleRate, semitonesToHz(MOTIF_ROOT_HZ * 4, 12), 0.4, 'glockenspiel', 0.22), t);
  }

  applyFades(out, 0.0005, 0.02);
  return normalize(out, 0.92);
}

/** A single tap, for movement and card-on-table. */
export function renderTap(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = impact(sampleRate, rng, opts.material ?? 'wood', 0.55);
  applyFades(out, 0.0005, 0.015);
  return normalize(out, 0.7);
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export function renderCardDraw(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.32);
  mixInto(out, noiseSweep(sampleRate, rng, 0.26, 3200, 900, 0.5), 0);
  mixInto(out, impact(sampleRate, rng, 'wood', 0.18), 0.2);
  applyFades(out);
  return normalize(out, 0.62);
}

export function renderCardFlip(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.26);
  mixInto(out, noiseSweep(sampleRate, rng, 0.14, 2600, 1400, 0.42), 0);
  mixInto(out, impact(sampleRate, rng, 'wood', 0.3), 0.12);
  applyFades(out);
  return normalize(out, 0.66);
}

export function renderCardShuffle(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 1.0);
  let t = 0;
  for (let i = 0; i < 12; i++) {
    mixInto(out, noiseSweep(sampleRate, rng, 0.06, rng.range(2400, 3600), 1100, 0.3), t);
    t += rng.range(0.055, 0.085);
  }
  applyFades(out);
  return normalize(out, 0.6);
}

/** Card transformation (GDD §5) — a seal breaking, played in reverse then forward. */
export function renderCardTransform(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.85);
  const pre = noiseSweep(sampleRate, rng, 0.3, 600, 4200, 0.35);
  mixInto(out, reverse(pre), 0);
  mixInto(out, impact(sampleRate, rng, 'stone', 0.9), 0.3);
  mixInto(out, subDrop(sampleRate, 180, 44, 0.5, 0.4), 0.3);
  mixInto(out, shimmerTone(sampleRate, MOTIF_ROOT_HZ * 4, 0.45, 18, 0.18), 0.34);
  applyFades(out);
  return normalize(out, 0.85);
}

// ---------------------------------------------------------------------------
// Combat
// ---------------------------------------------------------------------------

export function renderCombatHit(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.3);
  const thud = noiseBurst(sampleRate, rng, 0.001, 0.045, 0.9);
  lowpass(thud, 1400);
  mixInto(out, thud, 0);
  mixInto(out, modalRing(sampleRate, rng.range(90, 130), 6, 0.07, 0.55, 0.2), 0);
  mixInto(out, impact(sampleRate, rng, 'metal', 0.25), 0.002);
  applyFades(out, 0.0004, 0.02);
  return normalize(out, 0.88);
}

export function renderCombatCrit(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.5);
  mixInto(out, renderCombatHit(rng, opts), 0);
  mixInto(out, subDrop(sampleRate, 150, 40, 0.35, 0.55), 0.01);
  mixInto(out, struckTone(sampleRate, semitonesToHz(MOTIF_ROOT_HZ * 2, 12), 0.3, 'bell', 0.3), 0.02);
  applyFades(out, 0.0004, 0.03);
  return normalize(out, 0.94);
}

export function renderCombatMiss(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.22);
  const swish = noiseSweep(sampleRate, rng, 0.18, 5200, 1400, 0.4);
  mixInto(out, swish, 0);
  applyFades(out);
  return normalize(out, 0.5);
}

// ---------------------------------------------------------------------------
// Rewards, discovery, fate
// ---------------------------------------------------------------------------

/** Rarity signature (Audio Bible §4). Intervals come from the motif's scale. */
export function renderReward(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const sig = RARITY_SIGNATURES[opts.rarity ?? 'common'];
  const out = createBuffer(sampleRate, sig.duration + 0.9);
  const root = MOTIF_ROOT_HZ * 4; // up two octaves, so it sits above the mix
  const step = sig.duration / Math.max(1, sig.intervals.length);

  if (sig.reverse) {
    const pre = struckTone(sampleRate, root, 0.35, sig.timbre, 0.3);
    mixInto(out, reverse(pre), 0);
  }
  sig.intervals.forEach((semi, i) => {
    const freq = semitonesToHz(root, semi);
    const tone = struckTone(sampleRate, freq, sig.duration * 1.3, sig.timbre, 0.72 / (1 + i * 0.14));
    mixInto(out, tone, (sig.reverse ? 0.3 : 0) + i * step * 0.62);
  });
  if (sig.shimmer) {
    mixInto(out, shimmerTone(sampleRate, root * 3, sig.duration, 11, 0.14), sig.duration * 0.3);
  }
  if (sig.subDrop) {
    mixInto(out, subDrop(sampleRate, 140, 36, 0.7, 0.42), sig.duration * 0.45);
  }
  applyFades(out, 0.002, 0.08);
  return normalize(out, 0.8);
}

/**
 * Discovery (Audio Bible §5): must be instantly recognisable and must not collide
 * with any other sound, so it lives high and starts with a reverse swell — a
 * gesture used nowhere else in the game.
 */
export function renderDiscovery(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const major = (opts.rarity ?? 'legendary') !== 'common';
  const length = major ? 2.6 : 1.1;
  const out = createBuffer(sampleRate, length);
  const root = MOTIF_ROOT_HZ * 8;

  const swell = noiseSweep(sampleRate, rng, major ? 0.55 : 0.25, 900, 7000, 0.22);
  mixInto(out, reverse(swell), 0);

  const bells = major ? [0, 7, 12, 19] : [0, 7];
  bells.forEach((semi, i) => {
    mixInto(
      out,
      struckTone(sampleRate, semitonesToHz(root, semi), 1.6, 'bell', 0.5 / (1 + i * 0.2)),
      (major ? 0.5 : 0.22) + i * 0.075,
    );
  });
  if (major) mixInto(out, shimmerTone(sampleRate, root * 1.5, 1.4, 9, 0.1), 0.6);
  applyFades(out, 0.004, 0.2);
  return normalize(out, 0.76);
}

/** Fate Debt threshold — low, detuned, rising. Should feel like a bill arriving. */
export function renderFateDebt(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 1.1);
  mixInto(out, shimmerTone(sampleRate, MOTIF_ROOT_HZ * 0.5, 0.95, 42, 0.55), 0);
  mixInto(out, subDrop(sampleRate, 90, 150, 0.85, 0.38), 0.05);
  const pulse = modalRing(sampleRate, MOTIF_ROOT_HZ * 0.25, 4, 0.35, 0.5, 0.9);
  mixInto(out, pulse, 0);
  lowpass(out, 900);
  applyFades(out, 0.01, 0.15);
  return normalize(out, 0.8);
}

export function renderFateChain(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 1.0);
  const root = MOTIF_ROOT_HZ * 4;
  [0, 7, 12].forEach((semi, i) => {
    mixInto(out, struckTone(sampleRate, semitonesToHz(root, semi), 0.8, 'glockenspiel', 0.6), i * 0.09);
  });
  mixInto(out, shimmerTone(sampleRate, root * 2, 0.7, 8, 0.14), 0.1);
  applyFades(out, 0.002, 0.1);
  return normalize(out, 0.78);
}

/** Bad Luck — not a failure sting. A door opening in an unexpected direction. */
export function renderBadLuck(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 1.3);
  mixInto(out, subDrop(sampleRate, 200, 70, 0.5, 0.3), 0);
  // Minor third up, not down: something is being offered, not taken.
  [0, 3].forEach((semi, i) => {
    mixInto(out, struckTone(sampleRate, semitonesToHz(MOTIF_ROOT_HZ * 2, semi), 1.0, 'marimba', 0.5), 0.18 + i * 0.14);
  });
  applyFades(out, 0.004, 0.14);
  return normalize(out, 0.72);
}

/** Death (Audio Bible §5): everything filtered away, silence, then one low note. */
export function renderDeath(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 1.8);
  const collapse = noiseSweep(sampleRate, rng, 0.35, 4000, 180, 0.3);
  mixInto(out, collapse, 0);
  // 0.4 s of deliberate silence before the final note.
  mixInto(out, struckTone(sampleRate, MOTIF_ROOT_HZ * 0.5, 1.0, 'bell', 0.6), 0.9);
  lowpass(out, 2200);
  applyFades(out, 0.01, 0.25);
  return normalize(out, 0.74);
}

export function renderUiClick(rng: NoiseRng, opts: RenderOptions = {}): Buffer {
  const sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  const out = createBuffer(sampleRate, 0.09);
  const click = noiseBurst(sampleRate, rng, 0.0008, 0.012, 0.5);
  bandpass(click, 900, 5200);
  mixInto(out, click, 0);
  mixInto(out, modalRing(sampleRate, 1800, 8, 0.02, 0.25, 0.08), 0);
  applyFades(out, 0.0003, 0.01);
  return normalize(out, 0.4);
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const diceRollFor = (material: DiceMaterial): SfxSpec => ({
  id: `sfx.dice.roll.${material}`,
  bus: 'tactile',
  duration: 1.2,
  render: (rng, opts) => renderDiceRoll(rng, { ...opts, material }),
});

const rewardFor = (rarity: Rarity): SfxSpec => ({
  id: `sfx.reward.${rarity}`,
  bus: 'ui',
  duration: RARITY_SIGNATURES[rarity].duration,
  render: (rng, opts) => renderReward(rng, { ...opts, rarity }),
});

/** Every audio id the game may request. Missing ids fail loudly, not silently. */
export const SFX_REGISTRY: ReadonlyMap<string, SfxSpec> = new Map(
  (
    [
      diceRollFor('wood'),
      diceRollFor('stone'),
      diceRollFor('metal'),
      diceRollFor('magic'),
      { id: 'sfx.dice.bank.spend', bus: 'tactile', duration: 0.3, render: renderTap },
      { id: 'sfx.dice.reroll', bus: 'tactile', duration: 1.2, render: renderDiceRoll },
      { id: 'sfx.dice.hold', bus: 'tactile', duration: 0.3, render: renderTap },
      { id: 'sfx.dice.split', bus: 'tactile', duration: 0.3, render: renderTap },
      { id: 'sfx.dice.double', bus: 'tactile', duration: 1.2, render: renderDiceRoll },
      { id: 'sfx.dice.sacrifice', bus: 'tactile', duration: 0.85, render: renderCardTransform },
      { id: 'sfx.dice.gamble', bus: 'tactile', duration: 1.2, render: renderDiceRoll },
      { id: 'sfx.dice.bank', bus: 'tactile', duration: 0.3, render: renderTap },

      { id: 'sfx.card.draw', bus: 'tactile', duration: 0.3, render: renderCardDraw },
      { id: 'sfx.card.flip', bus: 'tactile', duration: 0.26, render: renderCardFlip },
      { id: 'sfx.card.tableTap', bus: 'tactile', duration: 0.15, render: renderTap },
      { id: 'sfx.card.shuffle', bus: 'tactile', duration: 1.0, render: renderCardShuffle },
      { id: 'sfx.card.transform', bus: 'tactile', duration: 0.85, render: renderCardTransform },
      // 전설·신화 카드를 낼 때 (Audio Bible §3). 엔진은 오래전부터 이 id 를 불렀는데 등록이 없어
      // 조용히 무시됐다 — sfx-ids 시험이 이제 그런 빈 id 를 잡는다.
      {
        id: 'sfx.card.rare',
        bus: 'tactile',
        duration: RARITY_SIGNATURES.legendary.duration,
        render: (r, o) => renderReward(r, { ...o, rarity: 'legendary' }),
      },

      { id: 'sfx.combat.hit', bus: 'world', duration: 0.3, render: renderCombatHit },
      { id: 'sfx.combat.crit', bus: 'world', duration: 0.5, render: renderCombatCrit },
      { id: 'sfx.combat.miss', bus: 'world', duration: 0.22, render: renderCombatMiss },
      { id: 'sfx.combat.appear', bus: 'world', duration: 0.6, render: (r, o) => renderReward(r, { ...o, rarity: 'uncommon' }) },
      { id: 'sfx.combat.bossAppear', bus: 'world', duration: 1.5, render: renderFateDebt },
      { id: 'sfx.combat.victory', bus: 'ui', duration: 1.0, render: (r, o) => renderReward(r, { ...o, rarity: 'rare' }) },

      rewardFor('common'),
      rewardFor('uncommon'),
      rewardFor('rare'),
      rewardFor('epic'),
      rewardFor('legendary'),
      rewardFor('mythic'),

      { id: 'sfx.discovery.minor', bus: 'ui', duration: 1.1, render: (r, o) => renderDiscovery(r, { ...o, rarity: 'common' }) },
      { id: 'sfx.discovery.major', bus: 'ui', duration: 2.6, render: renderDiscovery },

      { id: 'sfx.fate.debtThreshold', bus: 'world', duration: 1.1, render: renderFateDebt },
      { id: 'sfx.fate.chain', bus: 'ui', duration: 1.0, render: renderFateChain },
      { id: 'sfx.fate.badLuck', bus: 'ui', duration: 1.3, render: renderBadLuck },
      { id: 'sfx.fate.promiseBroken', bus: 'world', duration: 1.1, render: renderFateDebt },

      { id: 'sfx.move.step', bus: 'world', duration: 0.2, render: renderTap },
      { id: 'sfx.player.death', bus: 'world', duration: 1.8, render: renderDeath },
      { id: 'sfx.ui.click', bus: 'ui', duration: 0.09, render: renderUiClick },
    ] as SfxSpec[]
  ).map((s) => [s.id, s]),
);

export function getSfx(id: string): SfxSpec | undefined {
  return SFX_REGISTRY.get(id);
}

/** Deterministic render of any registered sound. */
export function renderSfx(id: string, seed: number, opts: RenderOptions = {}): Buffer | null {
  const spec = SFX_REGISTRY.get(id);
  if (!spec) return null;
  const buf = spec.render(new NoiseRng(seed), opts);
  // Bus gain is baked in for offline renders; the live engine applies it on the
  // graph instead, so the two paths stay consistent in relative level.
  return softClip(buf, 1.02) ;
}

export { BUSES };
